import { ignoreCancelledNavigation } from '@/lib/ignore-cancelled-navigation';
import { useAffiliateFirm } from '@commenda/api';
import { AffiliateFirmHelper } from '@commenda/utils';
import { useConfig } from 'context/useConfig';
import { useAffiliateViewer } from 'hooks/useAffiliateViewer';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useState } from 'react';
import {
  AffiliateAiPromptHelper,
  type TAffiliateAiScope,
} from './AffiliateAiPromptHelper';

interface UseAffiliateAiClientOptions {
  /** False = the caller resolved the A_AND_M_TP_DEMO flag as off: the firm fetch never fires, so
   * flag-off affiliate pages stay byte-identical on the wire. */
  enabled?: boolean;
}

/** The affiliate AI scope, carried in `?client=` (not `useConfig().companyId`, whose reconciliation
 * can overwrite ids outside the viewer's own links). Missing, `all` or an unknown id is firm scope. */
export const useAffiliateAiClient = ({
  enabled = true,
}: UseAffiliateAiClientOptions = {}) => {
  const router = useRouter();
  const { affiliateFirmId, user } = useConfig();
  const { membership } = useAffiliateViewer();
  const { data, error, isLoading } = useAffiliateFirm(
    enabled ? (affiliateFirmId ?? '') : ''
  );

  const affiliateFirm = data?.affiliateFirm;
  const userId = user?.id;
  const clients = useMemo(() => {
    if (!affiliateFirm) return [];
    const reachableClients =
      membership && userId
        ? AffiliateFirmHelper.getReachableClients(
            userId,
            membership.role,
            affiliateFirm
          )
        : affiliateFirm.clients;
    return [...reachableClients].sort((a, b) => a.name.localeCompare(b.name));
  }, [affiliateFirm, membership, userId]);

  const rawParam = router.query[AffiliateAiPromptHelper.CLIENT_QUERY_PARAM];
  const urlValue = typeof rawParam === 'string' ? rawParam : null;

  // An optimistic pick applies only while the URL still shows what it showed at pick time; once
  // the URL moves (the shallow push landed, or the user navigated elsewhere) it is spent.
  const [optimisticPick, setOptimisticPick] = useState<{
    value: string;
    urlValueAtPick: string | null;
  } | null>(null);
  const candidateValue =
    optimisticPick && urlValue === optimisticPick.urlValueAtPick
      ? optimisticPick.value
      : urlValue;
  const isScopePending = candidateValue !== urlValue;

  const parsedId =
    candidateValue !== null &&
    candidateValue !== AffiliateAiPromptHelper.ALL_CLIENTS_PARAM_VALUE
      ? Number(candidateValue)
      : null;
  const candidateId =
    parsedId !== null && Number.isInteger(parsedId) ? parsedId : null;

  const client =
    candidateId !== null
      ? (clients.find((firmClient) => firmClient.id === candidateId) ?? null)
      : null;
  const scope: TAffiliateAiScope = client ? 'client' : 'firm';

  const setClientId = useCallback(
    (clientId: number | null) => {
      const value =
        clientId === null
          ? AffiliateAiPromptHelper.ALL_CLIENTS_PARAM_VALUE
          : String(clientId);
      setOptimisticPick({ value, urlValueAtPick: urlValue });
      const query = { ...router.query };
      delete query.c;
      query[AffiliateAiPromptHelper.CLIENT_QUERY_PARAM] = value;
      router
        .push({ pathname: router.pathname, query }, undefined, {
          shallow: true,
        })
        .catch(ignoreCancelledNavigation);
    },
    [router, urlValue]
  );

  return {
    clients,
    client,
    clientId: client?.id ?? null,
    scope,
    /** True between an optimistic pick and the shallow push that drops `?c=` landing. */
    isScopePending,
    affiliateFirmName: affiliateFirm?.name ?? null,
    setClientId,
    isLoading,
    error,
  };
};
